import {
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubTrigger,
  ContextMenuSubContent,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
} from '@/components/ui/context-menu'
import {
  ArrowUpDown,
  Search,
  Filter,
  CheckCircle2,
  ChevronUp,
  ChevronDown,
  Circle,
  Clock,
  AlertCircle,
  Calendar,
  Flag,
  Layers,
  X,
  LayoutPanelLeft,
  Palette,
} from 'lucide-react'
import { Kbd, KbdGroup } from '@/components/ui/kbd'

const SORT_VALUE_LABEL = {
  date: 'Date',
  priority: 'Priority',
  status: 'Status',
}
const FILTER_VALUE_LABEL = {
  today: 'Today',
  tomorrow: 'Tomorrow',
  overdue: 'Overdue',
}

const getSortSummary = (sortBy) => SORT_VALUE_LABEL[sortBy] || 'Default'
const getFilterSummary = (filterBy) => FILTER_VALUE_LABEL[filterBy] || 'All'

const ContextMenuShortcutKeys = ({ keys }) => (
  <ContextMenuShortcut className="opacity-60 pl-4">
    <KbdGroup>
      {keys.map((key) => (
        <Kbd key={key}>{key}</Kbd>
      ))}
    </KbdGroup>
  </ContextMenuShortcut>
)
const ContextMenuItemIconLabel = ({ icon: Icon, label, iconClassName }) => (
  <>
    <Icon className={iconClassName || 'mr-2 h-4 w-4'} />
    <span>{label}</span>
  </>
)
const ContextMenuSubTriggerLabel = ({ icon: Icon, label, value }) => (
  <>
    <Icon className="mr-2 h-4 w-4" />
    <span>{label}</span>
    {value ? <span className="ml-2 text-xs text-muted-foreground">{value}</span> : null}
  </>
)

const TaskActionsMenu = ({ handleToggleAllDone, hasTodoTasks, hasDoneTasks }) => (
  <ContextMenuSub>
    <ContextMenuSubTrigger>
      <ContextMenuSubTriggerLabel icon={CheckCircle2} label="All" />
    </ContextMenuSubTrigger>
    <ContextMenuSubContent className="w-56">
      <ContextMenuItem onSelect={handleToggleAllDone} disabled={!hasTodoTasks && !hasDoneTasks}>
        <ContextMenuItemIconLabel
          icon={hasTodoTasks ? CheckCircle2 : Circle}
          label={hasTodoTasks ? 'Complete' : 'Reopen'}
        />
        <ContextMenuShortcutKeys keys={['⌘', hasTodoTasks ? 'D' : 'T']} />
      </ContextMenuItem>
    </ContextMenuSubContent>
  </ContextMenuSub>
)
const SortMenuItem = ({ value, label, icon: Icon }) => (
  <ContextMenuRadioItem value={value}>
    <ContextMenuItemIconLabel icon={Icon} label={label} />
  </ContextMenuRadioItem>
)
const SortMenuItems = ({ sortBy, handleSortChange, handleClearSort }) => (
  <ContextMenuRadioGroup value={sortBy || ''} onValueChange={handleSortChange}>
    <SortMenuItem value="date" label="Date" icon={Calendar} />
    <SortMenuItem value="priority" label="Priority" icon={Flag} />
    <SortMenuItem value="status" label="Status" icon={CheckCircle2} />
    <ContextMenuSeparator />
    <ContextMenuItem onSelect={handleClearSort}>
      <ContextMenuItemIconLabel icon={X} label="Default" />
    </ContextMenuItem>
  </ContextMenuRadioGroup>
)
const FilterMenuItem = ({ value, label, icon: Icon, iconClassName }) => (
  <ContextMenuRadioItem value={value}>
    <ContextMenuItemIconLabel icon={Icon} label={label} iconClassName={iconClassName} />
  </ContextMenuRadioItem>
)
const FilterMenuItems = ({ filterBy, handleFilterChange, handleClearFilter }) => (
  <ContextMenuRadioGroup value={filterBy || ''} onValueChange={handleFilterChange}>
    <FilterMenuItem value="today" label="Today" icon={Clock} />
    <FilterMenuItem value="tomorrow" label="Tomorrow" icon={Calendar} />
    <FilterMenuItem
      value="overdue"
      label="Overdue"
      icon={AlertCircle}
      iconClassName="mr-2 h-4 w-4 text-destructive"
    />
    <ContextMenuSeparator />
    <ContextMenuItem onSelect={handleClearFilter}>
      <ContextMenuItemIconLabel icon={Layers} label="Reset" />
    </ContextMenuItem>
  </ContextMenuRadioGroup>
)
const ViewSortSubMenu = ({ sortBy, handleSortChange, handleClearSort }) => (
  <ContextMenuSub>
    <ContextMenuSubTrigger>
      <ContextMenuSubTriggerLabel icon={ArrowUpDown} label="Sort" value={getSortSummary(sortBy)} />
    </ContextMenuSubTrigger>
    <ContextMenuSubContent className="w-48">
      <SortMenuItems
        sortBy={sortBy}
        handleSortChange={handleSortChange}
        handleClearSort={handleClearSort}
      />
    </ContextMenuSubContent>
  </ContextMenuSub>
)
const ViewFilterSubMenu = ({ filterBy, handleFilterChange, handleClearFilter }) => (
  <ContextMenuSub>
    <ContextMenuSubTrigger>
      <ContextMenuSubTriggerLabel icon={Filter} label="Filter" value={getFilterSummary(filterBy)} />
    </ContextMenuSubTrigger>
    <ContextMenuSubContent className="w-48">
      <FilterMenuItems
        filterBy={filterBy}
        handleFilterChange={handleFilterChange}
        handleClearFilter={handleClearFilter}
      />
    </ContextMenuSubContent>
  </ContextMenuSub>
)

export function GlobalContextMenuContent({
  handleToggleSidebar,
  showSidebar,
  handleToggleAllExpanded,
  anyExpanded,
  handleToggleAllDone,
  hasTodoTasks,
  hasDoneTasks,
  sortBy,
  handleSortChange,
  handleClearSort,
  filterBy,
  handleFilterChange,
  handleClearFilter,
  children,
}) {
  return (
    <ContextMenuContent className="w-64">
      {children}
      {children && <ContextMenuSeparator />}

      {/* Primary Actions */}
      <ContextMenuItem onSelect={handleToggleAllDone} disabled={!hasTodoTasks && !hasDoneTasks}>
        <ContextMenuItemIconLabel
          icon={hasTodoTasks ? CheckCircle2 : Circle}
          label={hasTodoTasks ? 'Complete all' : 'Reopen all'}
        />
        <ContextMenuShortcutKeys keys={['⌘', hasTodoTasks ? 'D' : 'T']} />
      </ContextMenuItem>

      <ContextMenuSeparator />

      {/* Group View & Filtering under a single "View" submenu if needed, or keep them top level if they are key */}
      <ViewSortSubMenu
        sortBy={sortBy}
        handleSortChange={handleSortChange}
        handleClearSort={handleClearSort}
      />
      <ViewFilterSubMenu
        filterBy={filterBy}
        handleFilterChange={handleFilterChange}
        handleClearFilter={handleClearFilter}
      />

      <ContextMenuSeparator />

      {/* Sidebar is a key UI action */}
      <ContextMenuItem onSelect={handleToggleSidebar}>
        <ContextMenuItemIconLabel
          icon={LayoutPanelLeft}
          label={showSidebar ? 'Hide Sidebar' : 'Show Sidebar'}
        />
        <ContextMenuShortcutKeys keys={['M']} />
      </ContextMenuItem>
    </ContextMenuContent>
  )
}
